import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const FORM_URL = 'https://readdy.ai/api/form/d72h9vsij602egd95i30';

export default function ContactForm() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(false);

    const form = e.currentTarget;
    const data = new URLSearchParams();

    Array.from(form.elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name) {
        data.append(input.name, input.value);
      }
    });

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl p-10 text-center flex flex-col items-center gap-4 h-full justify-center min-h-[500px]">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
          <i className="ri-check-line text-emerald-600 text-3xl"></i>
        </div>
        <h3 className="text-2xl font-bold text-slate-800">{t('contact_form_success_title')}</h3>
        <p className="text-slate-500 max-w-sm leading-relaxed">{t('contact_form_success_desc')}</p>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-phone-line text-sky-500 text-sm"></i>
          </div>
          <a href="tel:+15615122820" className="text-sky-600 font-semibold text-sm cursor-pointer">
            +1 (561) 512-2820
          </a>
        </div>
      </div>
    );
  }

  const serviceOptions = [
    'contact_form_service_opt0',
    'contact_form_service_opt1',
    'contact_form_service_opt2',
    'contact_form_service_opt3',
    'contact_form_service_opt4',
    'contact_form_service_opt5',
    'contact_form_service_opt6',
    'contact_form_service_opt7',
  ];

  return (
    <div className="bg-white rounded-2xl p-8 md:p-10">
      {/* Form header */}
      <div className="mb-7">
        <h2 className="text-2xl font-bold text-slate-800 mb-1.5">{t('contact_form_title')}</h2>
        <p className="text-slate-500 text-sm">{t('contact_form_sub')}</p>
      </div>

      <form
        id="contact-quote-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        {/* Name + Phone */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
              {t('contact_form_name')}
            </label>
            <input
              type="text"
              name="full_name"
              required
              placeholder={t('contact_form_ph_name')}
              className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
              {t('contact_form_phone')}
            </label>
            <input
              type="tel"
              name="phone"
              required
              placeholder={t('contact_form_ph_phone')}
              className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
            {t('contact_form_email')}
          </label>
          <input
            type="email"
            name="email"
            required
            placeholder={t('contact_form_ph_email')}
            className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition"
          />
        </div>

        {/* Address */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
            {t('contact_form_address')}
          </label>
          <input
            type="text"
            name="address"
            placeholder={t('contact_form_ph_address')}
            className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition"
          />
        </div>

        {/* Service */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
            {t('contact_form_service')}
          </label>
          <div className="relative">
            <select
              name="service"
              required
              defaultValue=""
              className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition appearance-none cursor-pointer bg-white"
            >
              {serviceOptions.map((key, i) => (
                <option key={key} value={i === 0 ? '' : t(key)} disabled={i === 0}>
                  {t(key)}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-down-s-line text-slate-400"></i>
            </div>
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
            {t('contact_form_message')}
          </label>
          <textarea
            name="message"
            rows={4}
            maxLength={500}
            placeholder={t('contact_form_ph_message')}
            onChange={(e) => setCharCount(e.target.value.length)}
            className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition resize-none"
          />
          <p className="text-right text-xs text-slate-400 mt-1">{charCount}/500</p>
        </div>

        {/* Error */}
        {error && (
          <p className="text-red-500 text-sm flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {t('contact_form_error')}
          </p>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-sky-600 hover:bg-sky-700 disabled:bg-sky-400 text-white font-bold py-4 rounded-lg text-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Sending...
            </>
          ) : (
            <>
              <i className="ri-send-plane-fill"></i>
              {t('contact_form_submit')}
            </>
          )}
        </button>

        <p className="text-center text-xs text-slate-400">{t('contact_form_privacy')}</p>
      </form>
    </div>
  );
}
